<template>
  <OneAlert
    v-if="isAuthenticationPopupVisible"
    :brandClass="brandClass()"
    :title="$t('profile_account_data_personalID_authorization_modal_title')"
    :content="$t('profile_account_data_personalID_authorization_modal_subtitle')"
    :class="brandClass()"
    :button_close_disabled="areButtonsDisabled"
    @close="closeAuthMethodsPopup()"
  >
    <template v-slot:internal-content>
      <div class="auth-method-options">
        <!-- PASSKEY OPTION -->
        <div
          v-if="isPasskeyOptionVisible"
          class="auth-method-option auth-method-option-passkey"
          :class="[
            brandClass(), 
            areButtonsDisabled && 'disabled'
          ]"
          role="button"
          :tabindex="areButtonsDisabled ? '-1' : '0'"
          @keydown="ariaClick"
          @click="!areButtonsDisabled && passkeyOptionSelected()"
        >
          <div class="auth-method-option-title-container">
            <div
              class="auth-method-option-title-icon"
              :class="brandClass()">
            </div>
            <div class="auth-method-option-title">
              {{$t("profile_account_data_personalID_authorization_modal_passkey_option_title")}}
            </div>
          </div>
          <div class="auth-method-option-subtitle-container">
            <div class="auth-method-option-subtitle">
              {{$t("profile_account_data_personalID_authorization_modal_passkey_option_subtitle")}}
            </div>
          </div>
        </div>

        <!-- 2FA OPTION -->
        <div
          v-if="isTwoFactorAuthOptionVisible"
          class="auth-method-option auth-method-option-2FA"
          :class="[
            brandClass(), 
            areButtonsDisabled && 'disabled'
          ]"
          role="button"
          :tabindex="areButtonsDisabled ? '-1' : '0'"
          @keydown="ariaClick"
          @click="!areButtonsDisabled && twoFactorAuthOptionSelected()"
        >
          <div class="auth-method-option-title-container">
            <div
              class="auth-method-option-title-icon"
              :class="brandClass()">
            </div>
            <div class="auth-method-option-title">
              {{$t("profile_account_data_personalID_authorization_modal_2FA_option_title")}}
            </div>
          </div>
          <div class="auth-method-option-subtitle-container">
            <div class="auth-method-option-subtitle">
              {{$t("profile_account_data_personalID_authorization_modal_2FA_option_subtitle")}}
            </div>
          </div>
        </div>

        <!-- PASSWORD OPTION -->
        <div
          class="auth-method-option auth-method-option-password"
          :class="[
            brandClass(), 
            areButtonsDisabled && 'disabled'
          ]"
          role="button"
          :tabindex="areButtonsDisabled ? '-1' : '0'"
          @keydown="ariaClick"
          @click="!areButtonsDisabled && passwordOptionSelected()"
        >
          <div class="auth-method-option-title-container">
            <div
              class="auth-method-option-title-icon"
              :class="brandClass()">
            </div>
            <div class="auth-method-option-title">
              {{$t("profile_account_data_personalID_authorization_modal_password_option_title")}}
            </div>
          </div>
          <div class="auth-method-option-subtitle-container">
            <div class="auth-method-option-subtitle">
              {{$t("profile_account_data_personalID_authorization_modal_password_option_subtitle")}}
            </div>
          </div>
        </div>
      </div>
    </template>
  </OneAlert>
</template>
<script>
import { defineComponent } from "vue";

import oneUtils from "../../../../gcdm-one-library/gcdm-one-utils/src/bootstrap";

export default defineComponent({
  name: "AuthMethodsPopup",
  inject: ["$c", "$t", "brandClass"],
  mixins: [oneUtils.accessibilityMixin, oneUtils.customValidationMixin],
  emit: ["passkeyOptionSelected", "twoFactorAuthOptionSelected", "passwordOptionSelected", "closeAuthMethodsPopup"],
  props: ["isAuthenticationPopupVisible", "isPasskeyOptionVisible", "isTwoFactorAuthOptionVisible", "areButtonsDisabled"],
  methods: {
    passkeyOptionSelected() {
      this.$emit("passkeyOptionSelected");
    },
    twoFactorAuthOptionSelected() {
      this.$emit("twoFactorAuthOptionSelected");
    },
    passwordOptionSelected() {
      this.$emit("passwordOptionSelected");
    },
    closeAuthMethodsPopup() {
      this.$emit("closeAuthMethodsPopup");
    }
  }
});
</script>
<style lang="scss">
.auth-method-options {
  margin-top: 1.5rem;

  .auth-method-option {
    border: 1px solid $grey;
    border-radius: 0.25rem;
    padding: 1.25rem;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }

    &:hover, &:focus {
      border-color: black;
      background-color: $grey6;
      cursor: pointer;
    }

    &.disabled {
      opacity: 40%;
      border: 1px solid $grey;
      background-color: unset;
      cursor: default;
    }

    &.__d {
      &:hover, &:focus {
        border-color: white;
        background-color: unset;
        // Workaround to not use border-width: 2px (it would visually shift the contents of the method-option, not a good UX!!!)
        box-shadow:inset 0px 0px 0px 1px #fff;
      }

      &.disabled {
        opacity: 40%;
        border: 1px solid $grey;
        box-shadow: unset;
        cursor: default;
      }
    }

    .auth-method-option-title-container {
      display: flex;
      align-items: center;

      .auth-method-option-title-icon {
        width: 24px;
        height: 24px;
        background-repeat: no-repeat;
        background-position: center;
        background-size: 24px;
        margin-right: 1rem;
      }

      .auth-method-option-title {
        font-size: 15px;
      }
    }

    &.auth-method-option-passkey {
      .auth-method-option-title-icon {
        background-image: url('~@/assets/default/light/passkey-circle-default-light.svg');

        &.__d {
          background-image: url('~@/assets/default/dark/passkey-circle-default-dark.svg');
        }
      }
    }

    &.auth-method-option-2FA {
      .auth-method-option-title-icon {
        background-image: url('~@/assets/default/light/two-factor-auth-key-default-light.svg');

        &.__d {
          background-image: url('~@/assets/default/dark/two-factor-auth-key-default-dark.svg');
        }
      }
    }

    &.auth-method-option-password {
      .auth-method-option-title-icon {
        background-image: url('~@/assets/default/light/password-lock-default-light.svg');

        &.__d {
          background-image: url('~@/assets/default/dark/password-lock-default-dark.svg');
        }
      }
    }

    .auth-method-option-subtitle-container {
      margin-top: 0.3rem;
      margin-left: calc(24px + 1rem);
      font-size: 12px;

      @media (max-width: 768px) {
        display: none;
      }
    }
  }
}
</style>
